"use client";

import * as React from "react";
import { Alignment, Fit, Layout, useRive } from "@rive-app/react-webgl2";
import { cn } from "@marketing-sites/utils/cn";
import {
  useCarouselRive,
  CarouselRivePlaceholder,
  staticImageObjectClassName,
  type CarouselRiveProps,
} from "./carousel.rive";
import { useCarousel } from "./carousel.context";
import { RIVE_WEBGL2_OPTIONS } from "../../components/rive/webgl2-options";

/**
 * A Rive animation (WebGL2) whose playback is driven by the carousel.
 *
 * Same API as `Carousel.Rive` but uses the WebGL2 renderer for better
 * performance with complex animations (meshes, blend modes, etc.).
 *
 * Loading is gated on the carousel's `isInView` signal: `useRive` (which fetches
 * the `.riv`) lives in the inner component, which only mounts once the carousel
 * nears the viewport. Until then the placeholder / slide backdrop shows.
 */
export function RiveWebGL(props: CarouselRiveProps) {
  const { isInView } = useCarousel();

  if (props.staticImage || !isInView) {
    return (
      <div className={cn("relative size-full", props.className)}>
        <CarouselRivePlaceholder
          placeholder={props.staticImage ?? props.placeholder}
          objectClassName={
            props.staticImage
              ? staticImageObjectClassName(props.fit, props.align)
              : undefined
          }
        />
      </div>
    );
  }

  return <RiveWebGLInner {...props} />;
}

function RiveWebGLInner({
  index,
  src,
  fit = "Cover",
  align = "Center",
  stateMachineName,
  artboard,
  className,
  placeholder,
}: CarouselRiveProps) {
  const { RiveComponent, rive } = useRive(
    {
      src,
      artboard,
      autoplay: false,
      stateMachines: stateMachineName ? [stateMachineName] : undefined,
      layout: new Layout({ fit: Fit[fit], alignment: Alignment[align] }),
    },
    RIVE_WEBGL2_OPTIONS,
  );

  const [isLoaded, setIsLoaded] = React.useState(false);

  React.useEffect(() => {
    if (rive && !isLoaded) {
      setIsLoaded(true);
    }
  }, [rive, isLoaded]);

  useCarouselRive(index, rive, stateMachineName, artboard);

  return (
    <div className={cn("relative size-full", className)}>
      {!isLoaded && <CarouselRivePlaceholder placeholder={placeholder} />}
      <RiveComponent className="size-full" />
    </div>
  );
}
